import React, { useEffect, useState } from 'react'
import { ConfirmDialog } from '../../../../shared/sso-workspace/confirm-dialog'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { serverMessage } from '../../../../shared/error/server-message'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { aiToolsAvailable, recycleWrite } from './ai-tools-api'
import { plural } from '../../../../shared/sso-workspace/format'
import { deleteBody, deleteQuestion, isTooMany, kindItems, kindNoun, tooManyText } from './ai-tools-words'

// Move telemetry rows to the recycle bin (R2, owner 2026-10-29: "delete and
// delete all matching ... and we need a recycle bin"): one row (its Delete),
// the selected rows, or all matching. Before anything is written a dry run
// counts what the selection moves ("Move 338 sessions to the recycle bin?
// Their records, postmortems and findings go with them. You can restore
// them from the recycle bin."); more than 10,000 says the limit and holds
// the button. Callers offer it only with SYS_AI_TOOLS_TELEMETRY delete.
//
// kind: 'session' | 'event' | 'postmortem' | 'finding'; selection: { ids }
// or { filter, except? } (recycleWrite's); onDone({ matched, changed }): the
// rows were moved.
export const DeleteDialog = ({ kind, selection, onCancel, onDone }) => {
  const [count, setCount] = useState({ state: 'loading' })
  const [busy, setBusy] = useState(false)
  const [error, failure, setError] = useErrorLine()
  const key = JSON.stringify([kind, selection])

  useEffect(() => {
    if (!aiToolsAvailable()) {
      setCount({ state: 'bad', text: "AI tools telemetry isn't available in this build, so nothing can be moved." })
      return undefined
    }
    let stale = false
    setCount({ state: 'loading' })
    recycleWrite('delete', kind, selection, { dryRun: true }).then(
      answer => { if (!stale) setCount({ state: 'ok', matched: Number((answer && answer.matched) || 0) }) },
      err => { if (!stale) setCount({ state: 'bad', text: isTooMany(err) ? tooManyText(kind) : serverMessage(err, "The count couldn't be read."), failure: err }) }
    )
    return () => { stale = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  const matched = count.state === 'ok' ? count.matched : null

  const confirm = async () => {
    if (busy || !matched) return
    setBusy(true)
    setError(null)
    let answer
    try {
      answer = await recycleWrite('delete', kind, selection)
    } catch (err) {
      setBusy(false)
      setError(isTooMany(err) ? tooManyText(kind) : serverMessage(err, 'Nothing was moved to the recycle bin.'), err)
      return
    }
    setBusy(false)
    // outside the try: the rows are moved, whatever the page does next
    onDone(answer || {})
  }

  return (
    <ConfirmDialog
      open
      title={deleteQuestion(kind, matched)}
      body={matched === 0 ? `None of these ${kindItems(kind)} can be moved: they are already in the recycle bin, or gone.` : deleteBody(kind, matched)}
      confirmText="Move to the recycle bin"
      busy={busy}
      confirmDisabled={count.state !== 'ok' || !matched}
      error={error}
      failure={failure}
      onConfirm={confirm}
      onCancel={onCancel}>
      {count.state === 'loading' && <p className="sw-hint" role="status">Counting…</p>}
      {count.state === 'bad' && <p className="text-danger small" role="alert">{count.text} <ErrorReference error={count.failure} /></p>}
    </ConfirmDialog>
  )
}

// What a move said: "Moved 338 sessions to the recycle bin."
export const movedText = (kind, answer) => {
  const changed = Number((answer && answer.changed) || 0)
  if (!changed) return `Nothing was moved: those ${kindItems(kind)} are already in the recycle bin, or gone.`
  return `Moved ${plural(changed, kindNoun(kind))} to the recycle bin.`
}
